import { Controller } from "@hotwired/stimulus"
import $ from "jquery"
import "datatables.net-bs5"

const PAGE_LENGTH_KEY = "datatable:pageLength"

export default class extends Controller {
  static values = { url: String }

  connect() {
    this.stateKey = `datatable:${this.element.id}:state`
    this.state = this.loadState()
    this.initialize()
    this.updateFilterCount()

    window.addEventListener("filters:apply", this.handleFilters)
    window.addEventListener("datatable:clear-search", this.handleClearSearch)
    window.addEventListener("datatable:length", this.handleGlobalLength)
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    window.removeEventListener("filters:apply", this.handleFilters)
    window.removeEventListener("datatable:clear-search", this.handleClearSearch)
    window.removeEventListener("datatable:length", this.handleGlobalLength)
    document.removeEventListener("turbo:before-cache", this.beforeCache)
  }

  // "Exibir" é preferência global: mudar numa tabela replica nas demais da
  // página na hora (e persiste via localStorage pras próximas).
  handleGlobalLength = (event) => {
    const { len, source } = event.detail || {}
    if (source === this.element.id || !this.table) return
    if (this.table.page.len() === len) return

    this.applyingGlobalLength = true
    this.table.page.len(len).draw()
    this.applyingGlobalLength = false
  }

  handleFilters = (event) => {
    this.state.filters = event.detail
    this.saveState()
    this.updateFilterCount()
    this.table.ajax.reload()
  }

  handleClearSearch = (event) => {
    if (event.detail?.tableId && event.detail.tableId !== this.element.id) return
    this.table.search("").draw()
  }

  readColumns() {
    const raw = this.element.dataset.columns
    if (!raw) return undefined
    try {
      return JSON.parse(raw)
    } catch {
      return undefined
    }
  }

  readOrder() {
    const raw = this.element.dataset.order
    if (!raw) return undefined
    try {
      const { name, dir } = JSON.parse(raw)
      const cols = this.readColumns() || []
      const index = cols.findIndex((c) => c.name === name)
      return index >= 0 ? [[index, dir === "desc" ? "desc" : "asc"]] : undefined
    } catch {
      return undefined
    }
  }

  initialize() {
    if ($.fn.DataTable.isDataTable(this.element)) return

    const pageLength = this.loadPageLength()

    const controller = this
    this.table = $(this.element).DataTable({
      processing: true,
      serverSide: true,

      columns: this.readColumns(),

      order: this.readOrder() || [],

      pageLength: pageLength,

      layout: {
        topStart: "search",
        topEnd: "pageLength"
      },

      displayStart: this.state?.page
        ? this.state.page * pageLength
        : 0,

      ajax: {
        url: this.urlValue,
        data: (d) => {
          if (this.state?.filters && Object.keys(this.state.filters).length > 0) {
            d.filtros = this.state.filters
          }
        }
      },

      drawCallback: function () { controller.renderGroups(this.api()) }
    })


    this.table.on("page.dt", () => {
      this.state.page = this.table.page()
      this.saveState()
    })

    this.table.on("length.dt", (e, settings, len) => {
      this.savePageLength(len)
      this.state.page = this.table.page()
      this.saveState()

      if (!this.applyingGlobalLength) {
        window.dispatchEvent(new CustomEvent("datatable:length", {
          detail: { len, source: this.element.id }
        }))
      }
    })
  }

  renderGroups(table) {
    const rows = table.rows({ page: "current" })
    let previousGroup

    rows.every(function () {
      const group = this.data()?.DT_RowData?.group
      if (!group || group === previousGroup) return

      const header = document.createElement("tr")
      header.className = "table-secondary fw-semibold"
      const cell = document.createElement("td")
      cell.colSpan = table.columns().count()
      cell.textContent = group
      header.appendChild(cell)
      this.node().before(header)
      previousGroup = group
    })
  }

  loadPageLength() {
    const saved = parseInt(localStorage.getItem(PAGE_LENGTH_KEY), 10)
    return Number.isFinite(saved) && saved > 0 ? saved : 25
  }

  savePageLength(len) {
    localStorage.setItem(PAGE_LENGTH_KEY, String(len))
  }

  updateFilterCount() {
    const badge = this.element
      .closest(".card")
      ?.querySelector("[data-filter-count]")

    if (!badge) return

    const filters = this.state?.filters || {}

    const count = Object.values(filters).reduce((acc, val) => {
      if (Array.isArray(val)) return acc + val.length
      return acc + 1
    }, 0)

    if (count > 0) {
      badge.textContent = count
      badge.classList.remove("d-none")
    } else {
      badge.classList.add("d-none")
    }
  }

  saveState() {
    if (!this.state.filters || Object.keys(this.state.filters).length === 0) {
      delete this.state.filters
    }
    localStorage.setItem(this.stateKey, JSON.stringify(this.state))
  }

  loadState() {
    const saved = localStorage.getItem(this.stateKey)
    return saved ? JSON.parse(saved) : {}
  }

  beforeCache = () => {
    if ($.fn.DataTable.isDataTable(this.element)) {
      $(this.element).DataTable().destroy()
    }
  }
}
